import React from 'react';
import { Card, Checkbox, Input, Button } from '../../atoms';
import { Text, ActionText } from '../../typography';
import { useTranslate } from '@federico.moretti/react-translate';
import { FlexWrapper, useClickOutside, Divider, SPACINGS } from '../../utils';
import { IconWrapper, IconX, IconPlus, IconCheck } from '../../icons';
import styled, { useTheme } from 'styled-components/macro';

const WatchlistStyled = styled.div`
  width: 350px;

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: flex;
    align-items: center;
    margin-bottom: ${SPACINGS[2]};
  }

  li:last-child {
    margin-bottom: 0;
  }

  input[type='checkbox'] {
    margin-right: ${SPACINGS[2]};
  }

  .create {
    margin-top: ${SPACINGS[4]};
  }

  .watchlistsList {
    max-height: 250px;
    overflow: auto;
  }
`;

type WatchlistMenuProps = {
  watchlists: {
    id: string;
    name: string;
    isDefault: boolean;
    isItemSelected: boolean;
  }[];
  onRemove: (id: string) => void;
  onAdd: (id: string) => void;
  onClose: () => void;
  onCreate: (name: string) => void;
  ignoreRef?: React.RefObject<HTMLElement>;
  style?: React.CSSProperties;
};
function WatchlistMenu(props: WatchlistMenuProps) {
  const {
    watchlists,
    onClose,
    onCreate,
    onAdd,
    onRemove,
    ignoreRef,
    style,
  } = props;
  const theme = useTheme();
  const { withPrefix } = useTranslate();
  const t = withPrefix('components.watchlist');

  const containerRef = React.useRef<HTMLDivElement>(null);
  useClickOutside({
    ref: containerRef,
    handler: onClose,
    ignoreRef,
  });

  const [isInputVisible, setIsInputVisible] = React.useState(false);
  const [isSuccessMessageVisible, setIsSuccessMessageVisible] = React.useState(
    false
  );
  const [inputValue, setInputValue] = React.useState('');

  const timeoutId = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  function handleTimeoutClose() {
    timeoutId.current = setTimeout(() => {
      onClose();
      setInputValue('');
    }, 5000);
  }

  function handleAddWatchlist() {
    onCreate(inputValue);
    setIsInputVisible(false);
    setIsSuccessMessageVisible(true);

    handleTimeoutClose();
  }

  function handleEnterKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === 'Space') {
      onCreate(inputValue);
      setIsInputVisible(false);
      setIsSuccessMessageVisible(true);

      handleTimeoutClose();
    }
  }

  function handleCheckboxChange(
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) {
    if (e.target.checked) {
      onAdd(id);
    } else {
      onRemove(id);
    }
  }

  return (
    <WatchlistStyled style={style} ref={containerRef}>
      {isSuccessMessageVisible ? (
        <Card style={{ backgroundColor: theme.colors.white }}>
          <FlexWrapper style={{ marginBottom: SPACINGS[4] }}>
            <Text margin="none" type="label" size="sm">
              {t('successfullyAdded')}
            </Text>
            <IconWrapper type="action" onClick={onClose}>
              <IconX scale={0.8} />
            </IconWrapper>
          </FlexWrapper>
          <Text margin="none">
            <IconCheck stroke={theme.colors.primary} /> {inputValue}
          </Text>
        </Card>
      ) : (
        <Card style={{ backgroundColor: theme.colors.white }}>
          <FlexWrapper style={{ marginBottom: SPACINGS[4] }}>
            <Text margin="none" type="label" size="sm">
              {t('addToWatchlist')}
            </Text>
            <IconWrapper type="action" onClick={onClose}>
              <IconX scale={0.8} />
            </IconWrapper>
          </FlexWrapper>
          <ul>
            {watchlists
              .filter((wl) => wl.isDefault)
              .map((wl) => (
                <li key={wl.id}>
                  <Checkbox
                    name={wl.id}
                    defaultChecked={wl.isItemSelected}
                    onChange={(e) => handleCheckboxChange(e, wl.id)}
                  />
                  <Text margin="none">{wl.name}</Text>
                </li>
              ))}
          </ul>
          <Divider />
          <ul className="watchlistsList">
            {watchlists
              .filter((wl) => !wl.isDefault)
              .map((wl) => (
                <li key={wl.id}>
                  <Checkbox
                    name={wl.id}
                    defaultChecked={wl.isItemSelected}
                    onChange={(e) => handleCheckboxChange(e, wl.id)}
                  />
                  <Text margin="none">{wl.name}</Text>
                </li>
              ))}
          </ul>
          <div className="create">
            {!isInputVisible ? (
              <ActionText
                size="sm"
                type="action"
                onClick={() => setIsInputVisible(true)}
                iconLeft={
                  <IconPlus scale={0.75} stroke={theme.colors.primary20b} />
                }
              >
                {t('createNewWatchlist')}
              </ActionText>
            ) : (
              <div onKeyDown={handleEnterKeyDown}>
                <Text type="label" htmlFor="new-watchlist">
                  {t('watchlistName')}
                </Text>
                <Input
                  autoFocus
                  name="new-watchlist"
                  onChange={(e) => setInputValue(e.target.value)}
                  style={{ marginBottom: SPACINGS[4] }}
                />
                <Button
                  disabled={inputValue.length === 0}
                  styleType="outline"
                  type="action"
                  onClick={handleAddWatchlist}
                >
                  {t('createWatchlist')}
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}
    </WatchlistStyled>
  );
}

export default WatchlistMenu;
